import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useEffect } from 'react';
import Confetti from 'react-confetti';
import { useDefenderStore } from '@app/stores/defenderStore';
import { useAuthStore } from '@app/stores/authStore';
import { useUpgradeDefender } from '@hooks';
import { useWindowSize } from '@/shared/hooks/useWindowSize';
import { showToast } from '@/utils/toast';

// Background images
import bgArcher from '@/assets/home/components/solider-detail/bg-archer.webp';
import bgFireMage from '@/assets/home/components/solider-detail/bg-fire-mage.webp';
import bgIceMage from '@/assets/home/components/solider-detail/bg-ice-mage.webp';
import bgGunner from '@/assets/home/components/solider-detail/bg-gunner.webp';

// Icons
import icDamage from '@/assets/home/components/solider-detail/ic-damage.webp';
import icAttackTime from '@/assets/home/components/solider-detail/ic-attack-time.webp';
// import icAreaDamage from '@/assets/home/components/solider-detail/ic-area-damage.webp';
import icUpgrade from '@/assets/home/components/solider-detail/ic-upgrade.webp';
import icPrev from '@/assets/home/components/solider-detail/ic-prev.webp';
import icNext from '@/assets/home/components/solider-detail/ic-next.webp';
import goldIcon from '@/assets/header/gold.webp';

// Soldier images (for character display)
import archerImg from '@/assets/home/components/solider-detail/detail-archer.webp';
import fireMageImg from '@/assets/home/components/solider-detail/detail-fire-mage.webp';
import iceMageImg from '@/assets/home/components/solider-detail/detail-ice-mage.webp';
import gunnerImg from '@/assets/home/components/solider-detail/detail-gunner.webp';
import CloseButton from '@/assets/svgs/CloseButton';

export type SoldierType = 'archer' | 'fireMage' | 'iceMage' | 'gunner';

// Map UI soldier types to API defender IDs
const soldierToDefenderIdMap: Record<SoldierType, number> = {
  archer: 1,
  fireMage: 2,
  iceMage: 3,
  gunner: 4,
};

// Map soldier types to display names
const soldierDisplayNames: Record<SoldierType, string> = {
  archer: 'Archer',
  fireMage: 'Fire Mage',
  iceMage: 'Ice Mage',
  gunner: 'Gunner',
};

// Map soldier types to assets
const soldierAssets: Record<
  SoldierType,
  { backgroundImage: string; characterImage: string }
> = {
  archer: { backgroundImage: bgArcher, characterImage: archerImg },
  fireMage: { backgroundImage: bgFireMage, characterImage: fireMageImg },
  iceMage: { backgroundImage: bgIceMage, characterImage: iceMageImg },
  gunner: { backgroundImage: bgGunner, characterImage: gunnerImg },
};

interface SoldierUpgradeModalProps {
  isOpen: boolean;
  initialSoldier?: SoldierType;
  onClose: () => void;
  onUpgrade?: (soldierType: SoldierType) => void;
  onUpgradeSuccess?: () => void;
  isLoading?: boolean;
}

const soldierOrder: SoldierType[] = ['archer', 'fireMage', 'iceMage', 'gunner'];

export const SoldierUpgradeModal = ({
  isOpen,
  initialSoldier = 'archer',
  onClose,
  onUpgrade,
  onUpgradeSuccess,
  isLoading = false,
}: SoldierUpgradeModalProps) => {
  const [currentSoldierIndex, setCurrentSoldierIndex] = useState(
    soldierOrder.indexOf(initialSoldier)
  );
  const [showConfetti, setShowConfetti] = useState(false);

  const { width, height } = useWindowSize();

  useEffect(() => {
    const newIndex = soldierOrder.indexOf(initialSoldier);
    if (newIndex !== -1) {
      setCurrentSoldierIndex(newIndex);
    }
  }, [initialSoldier]);

  // Reset confetti when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowConfetti(false);
    }
  }, [isOpen]);

  const currentSoldier = soldierOrder[currentSoldierIndex];

  const { getDefenderDataById, userDefenders } = useDefenderStore();
  const { user } = useAuthStore();

  const upgradeMutation = useUpgradeDefender({
    onSuccess: () => {
      setShowConfetti(true);
      onUpgradeSuccess?.();
    },
  });

  const soldierData = useMemo(() => {
    const defenderId = soldierToDefenderIdMap[currentSoldier];
    const defenderData = getDefenderDataById(defenderId);

    if (!defenderData) {
      // Fallback to default values if data not loaded yet
      const defaultLevel = 1;
      return {
        name: soldierDisplayNames[currentSoldier],
        level: defaultLevel,
        maxLevel: 5,
        upgradeCost: 100, // Level 1 upgrade cost
        stats: {
          damage: 0,
          damageIncrease: 0,
          attackTime: 0,
          attackTimeIncrease: 0,
          special: 'Single',
          specialIcon: '🎯',
          specialIncrease: 0,
        },
        ...soldierAssets[currentSoldier],
      };
    }

    const { baseStats, userProgress } = defenderData;

    // Calculate bonus damage: damagePerLevel * (level - 1)
    // damagePerLevel is the additional damage per level (not a multiplier)
    const bonusDamage = Math.round(
      baseStats.damagePerLevel * (userProgress.level - 1)
    );

    // Current total damage = base + bonus
    const currentTotalDamage = baseStats.damage + bonusDamage;

    const getSpecialEffectDisplay = (
      effect?: string,
      metadata?: Array<Record<string, number>>
    ): { text: string; icon: string } => {
      if (!effect)
        return {
          text: 'Single target',
          icon: '🎯',
        };

      switch (effect.toLowerCase()) {
        case 'burn': {
          const burnDuration = metadata?.[0]?.duration || 3;
          return {
            text: `Burn (${burnDuration}s)`,
            icon: '🔥',
          };
        }
        case 'slow': {
          // skillIndex is the slow percentage (0.3 = 30% slower)
          const slowDuration = metadata?.[0]?.duration || 2;
          return {
            text: `Slow (${slowDuration}s)`,
            icon: '❄️',
          };
        }
        case 'aoe-splash': {
          const radius = metadata?.[0]?.skillIndex || 1.5;
          return {
            text: `Splash (${radius}m)`,
            icon: '💥',
          };
        }
        default:
          return {
            text: 'Single',
            icon: '🎯',
          };
      }
    };

    const specialEffect = getSpecialEffectDisplay(
      baseStats.specialEffect,
      baseStats.specialMetadata
    );

    // Upgrade cost increases per level: 100, 200, 400, 800, 1600...
    const getUpgradeCost = (level: number): number => {
      const costs: Record<number, number> = {
        1: 100,
        2: 200,
        3: 400,
        4: 800,
        5: 1600,
      };
      return costs[level] || 100 * Math.pow(2, level - 1);
    };

    const upgradeCost = getUpgradeCost(userProgress.level);

    return {
      name: soldierDisplayNames[currentSoldier],
      level: userProgress.level,
      maxLevel: baseStats.maxLevel,
      upgradeCost,
      stats: {
        damage: currentTotalDamage, // Total damage at current level
        damageIncrease: bonusDamage, // Bonus damage from levels
        attackTime: Math.round(baseStats.attackSpeed * 10) / 10,
        attackTimeIncrease: 0, // Attack speed doesn't increase per level
        special: specialEffect.text,
        specialIcon: specialEffect.icon,
        specialIncrease: 0, // Special effect doesn't change per level
      },
      ...soldierAssets[currentSoldier],
    };
  }, [currentSoldier, userDefenders, getDefenderDataById]);

  const handlePrev = () => {
    if (!isLoading) {
      setCurrentSoldierIndex((prev) =>
        prev === 0 ? soldierOrder.length - 1 : prev - 1
      );
    }
  };

  const handleNext = () => {
    if (!isLoading) {
      setCurrentSoldierIndex((prev) =>
        prev === soldierOrder.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleUpgrade = () => {
    if (isLoading || upgradeMutation.isPending) return;

    // Check if user has enough gold
    const userGold = user?.gold ?? 0;
    if (userGold < soldierData.upgradeCost) {
      showToast.error("Oops! You're broke! 💸 Farm some gold first!");
      return;
    }

    const defenderId = soldierToDefenderIdMap[currentSoldier];

    // Call upgrade API
    upgradeMutation.mutate(defenderId);

    // Legacy callback
    if (onUpgrade) {
      onUpgrade(currentSoldier);
    }
  };

  const isUpgrading = isLoading || upgradeMutation.isPending;
  const isMaxLevel = soldierData.level >= (soldierData.maxLevel || 5);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isUpgrading ? onClose : undefined}
            style={{ cursor: isUpgrading ? 'not-allowed' : 'default' }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[101] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] h-[516px] flex flex-col items-center pb-6 isolate bg-[#4E6A91] border-[3px] border-[#2E405633] rounded-2xl pointer-events-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Background Image */}
              <div className="relative w-full h-[335px] mb-[-24px] rounded-t-[14px] overflow-hidden border-b-2 border-[#2E4056]">
                <motion.img
                  key={currentSoldier}
                  src={soldierData.backgroundImage}
                  alt={soldierData.name}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Close Button */}
              <motion.button
                className="absolute right-2 top-[7.5px] w-6 h-6 rounded-full flex items-center justify-center z-50"
                style={{
                  background:
                    'linear-gradient(135deg, #FF0000 0%, #D70000 49.04%, #FF6F6F 100%)',
                  cursor: isUpgrading ? 'not-allowed' : 'pointer',
                  opacity: isUpgrading ? 0.5 : 1,
                }}
                whileHover={!isUpgrading ? { scale: 1.1 } : {}}
                whileTap={!isUpgrading ? { scale: 0.9 } : {}}
                onClick={!isUpgrading ? onClose : undefined}
              >
                <div className="pointer-events-none">
                  <CloseButton />
                </div>
              </motion.button>

              {/* Character Image */}
              <motion.img
                key={`char-${currentSoldier}`}
                src={soldierData.characterImage}
                alt={soldierData.name}
                className="absolute left-[70px] right-[69px] top-[103px] h-[200px] object-contain z-10"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              />

              {/* Title */}
              <h2
                className="absolute left-0 right-0 top-[14px] w-full h-8 font-lilita text-[28px] leading-8 text-center text-white"
                style={{ textShadow: '1px 2px 0px #000000' }}
              >
                {soldierData.name} (Level {soldierData.level})
              </h2>

              {/* Prev Arrow */}
              <motion.button
                onClick={handlePrev}
                disabled={isUpgrading}
                className="absolute left-[-20px] top-[153px] w-8 h-8"
                style={{
                  cursor: isUpgrading ? 'not-allowed' : 'pointer',
                  opacity: isUpgrading ? 0.5 : 1,
                }}
                whileHover={!isUpgrading ? { scale: 1.1, x: -3 } : {}}
                whileTap={!isUpgrading ? { scale: 0.95 } : {}}
              >
                <img src={icPrev} alt="Previous" className="w-full h-full" />
              </motion.button>

              {/* Next Arrow */}
              <motion.button
                onClick={handleNext}
                disabled={isUpgrading}
                className="absolute right-[-20px] top-[153px] w-8 h-8"
                style={{
                  cursor: isUpgrading ? 'not-allowed' : 'pointer',
                  opacity: isUpgrading ? 0.5 : 1,
                }}
                whileHover={!isUpgrading ? { scale: 1.1, x: 3 } : {}}
                whileTap={!isUpgrading ? { scale: 0.95 } : {}}
              >
                <img src={icNext} alt="Next" className="w-full h-full" />
              </motion.button>

              {/* Bottom Content Container */}
              <div className="flex flex-col items-center px-5 gap-4 w-full mt-auto z-20">
                {/* Upgrade Button */}
                <motion.button
                  onClick={handleUpgrade}
                  disabled={isUpgrading || isMaxLevel}
                  className="relative flex flex-row justify-center items-center px-5 py-2 gap-2 w-fit h-11 rounded-xl border-[3px] border-black"
                  style={{
                    background: isMaxLevel
                      ? 'linear-gradient(138.68deg, #FFD700 6.18%, #FFA500 95.61%)'
                      : 'linear-gradient(138.68deg, #04F00C 6.18%, #00B806 95.61%)',
                    cursor:
                      isUpgrading || isMaxLevel ? 'not-allowed' : 'pointer',
                    opacity: isUpgrading ? 0.7 : 1,
                    paddingLeft: isMaxLevel ? '20px' : '36px',
                  }}
                  whileHover={
                    !isUpgrading && !isMaxLevel ? { scale: 1.03, y: -2 } : {}
                  }
                  whileTap={!isUpgrading && !isMaxLevel ? { scale: 0.97 } : {}}
                  transition={{ duration: 0.15 }}
                >
                  {/* Inner highlight */}
                  <div className="absolute left-1 right-1 top-1 h-7 bg-white/30 rounded-lg" />

                  {/* Upgrade icon */}
                  {!isMaxLevel && (
                    <img
                      src={icUpgrade}
                      alt="Upgrade"
                      className="absolute w-auto h-[52px] z-10 -left-2 object-contain"
                      style={{ filter: 'drop-shadow(2px 3px 0px #000000)' }}
                    />
                  )}

                  {/* Max Level Glowing Star Icon */}
                  {isMaxLevel && (
                    <span
                      className="relative text-[32px] leading-8 z-10"
                      style={{
                        filter: 'drop-shadow(1px 2px 0px #000000)',
                      }}
                    >
                      🌟
                    </span>
                  )}

                  {/* Button text */}
                  <span
                    className="relative font-lilita text-[28px] leading-8 text-white z-10"
                    style={{ WebkitTextStroke: '1.7px #000' }}
                  >
                    {isMaxLevel
                      ? 'Max Level'
                      : isUpgrading
                        ? 'Upgrading...'
                        : 'Upgrade'}
                  </span>

                  {/* Cost */}
                  {!isMaxLevel && (
                    <div className="relative flex items-center gap-1 z-10">
                      <img src={goldIcon} alt="Gold" className="w-7 h-7" />
                      <span
                        className="font-lilita text-[28px] leading-8 text-white"
                        style={{ WebkitTextStroke: '1.7px #000' }}
                      >
                        {soldierData.upgradeCost}
                      </span>
                    </div>
                  )}
                </motion.button>

                {/* Stats Container */}
                <div className="flex flex-row items-stretch gap-4 w-[299px]">
                  {/* Damage */}
                  <StatCard
                    icon={icDamage}
                    label="Damage"
                    value={soldierData.stats.damage}
                    increase={soldierData.stats.damageIncrease}
                  />

                  {/* Attack Time */}
                  <StatCard
                    icon={icAttackTime}
                    label="Attack speed"
                    value={soldierData.stats.attackTime}
                    increase={soldierData.stats.attackTimeIncrease}
                  />

                  {/* Special Effect */}
                  <StatCard
                    emojiIcon={soldierData.stats.specialIcon}
                    label="Effect"
                    value={soldierData.stats.special}
                    increase={soldierData.stats.specialIncrease}
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Confetti Effect - Same as ChestRewardModal */}
          {showConfetti && (
            <div className="fixed inset-0 z-[102] pointer-events-none">
              <Confetti
                width={width}
                height={height}
                recycle={false}
                numberOfPieces={500}
                gravity={0.3}
                colors={['#FFD700', '#FFA500', '#FF6347', '#00FF2B', '#04F00C']}
              />
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

interface StatCardProps {
  icon?: string;
  emojiIcon?: string;
  label: string;
  value: number | string;
  increase: number;
}

const StatCard = ({
  icon,
  emojiIcon,
  label,
  value,
  increase,
}: StatCardProps) => (
  <motion.div
    className="flex flex-col items-center py-2 px-1 flex-1 bg-[#BBCDDE] rounded-xl"
    style={{
      boxShadow:
        '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    }}
    whileHover={{
      scale: 1.05,
      y: -2,
      boxShadow:
        '0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    }}
    transition={{ duration: 0.15 }}
  >
    {/* Icon */}
    <div
      className="flex items-center justify-center w-10 h-10 mb-2"
      style={{ textShadow: '1px 1px 0px #000000' }}
    >
      {emojiIcon ? (
        <span className="text-[32px] leading-[40px]">{emojiIcon}</span>
      ) : (
        <img src={icon} alt={label} className="w-10 h-10" />
      )}
    </div>

    {/* Label */}
    <p
      className="w-full font-lilita text-[18px] leading-[21px] text-center text-[#2E4056] mb-1 px-1"
      style={{
        textShadow: '-1px 1px 2px #ffffff40',
        // WebkitTextStroke: '0.3px #fff',
      }}
    >
      {label}
    </p>

    <div className="flex-1" />

    {/* Values */}
    <div
      className="flex items-center justify-center gap-1 flex-wrap"
      style={{
        textShadow: '-1px 1px 2px #000000',
        // WebkitTextStroke: '0.3px #000',
      }}
    >
      <span
        className={`font-lilita ${label === 'Effect' ? 'text-[16px]' : 'text-[18px]'} leading-[21px] text-white text-center`}
        style={{ wordBreak: 'break-word' }}
      >
        {value}
      </span>
      {label === 'Damage' && increase > 0 && (
        <span
          className="font-lilita text-[18px] leading-[21px] whitespace-nowrap"
          style={{ color: '#00FF2B', borderColor: '#003E0A' }}
        >
          (+{increase})
        </span>
      )}
    </div>
  </motion.div>
);
